import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SITE } from "../constants/site";
import { ShieldCheck, ExternalLink } from "lucide-react";

const sections = [
    { id: "introduction", label: "1. Introduction" },
    { id: "responsible-party", label: "2. Responsible Party & Information Officer" },
    { id: "information-collected", label: "3. Information We Collect" },
    { id: "lawful-basis", label: "4. Lawful Basis & Purpose" },
    { id: "sharing-storage", label: "5. Sharing, Storage & Security" },
    { id: "children-data", label: "6. Children's Data" },
    { id: "user-rights", label: "7. Your Rights Under POPIA" },
    { id: "cookies-retention", label: "8. Cookies & Retention" },
    { id: "cross-border", label: "9. Cross-Border Transfers" },
    { id: "complaints", label: "10. Complaints & Regulator Contact" },
    { id: "changes", label: "11. Changes to this Policy" },
    { id: "contact", label: "12. Contact Us" },
];

export default function Privacy() {
    const [activeId, setActiveId] = useState(sections[0].id);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                });
            },
            { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
        );
        sections.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    return (
        <div data-testid="privacy-page" className="min-h-screen bg-background">
            <Navbar />

            {/* Header */}
            <section className="pt-32 pb-12 border-b border-slate-200 bg-white">
                <div className="max-w-6xl mx-auto px-6 sm:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                        <ShieldCheck className="h-4 w-4 text-brand-teal" />
                        POPIA COMPLIANT
                    </div>
                    <h1 className="font-display mt-5 text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
                        Privacy Policy
                    </h1>
                    <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
                        This Privacy Policy explains how {SITE.company}{" "}
                        ("EmberCore Digital", "we", "us" or "our") collects,
                        uses, discloses and protects Personal Information when
                        you use the {SITE.appName} mobile application and
                        related services (the "Service"). It is prepared in
                        accordance with the Protection of Personal Information
                        Act, 2013 ("POPIA") of the Republic of South Africa.
                    </p>
                    <p className="mt-3 text-sm text-slate-500">
                        Effective date: {SITE.effectiveDate} · Last updated:{" "}
                        {SITE.effectiveDate}
                    </p>
                </div>
            </section>

            {/* Content */}
            <section className="py-16">
                <div className="max-w-6xl mx-auto px-6 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-12">
                        {/* Sidebar TOC */}
                        <aside className="lg:col-span-4 xl:col-span-3">
                            <nav
                                data-testid="privacy-toc"
                                className="lg:sticky lg:top-24 rounded-2xl border border-slate-200 bg-white p-5"
                            >
                                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">
                                    On this page
                                </div>
                                <ul className="space-y-1.5 text-sm">
                                    {sections.map((s) => (
                                        <li key={s.id}>
                                            <a
                                                href={`#${s.id}`}
                                                data-testid={`toc-link-${s.id}`}
                                                className={`block rounded-lg px-3 py-2 transition-all border-l-2 ${
                                                    activeId === s.id
                                                        ? "border-brand-purple text-slate-900 font-semibold bg-slate-50"
                                                        : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                                                }`}
                                            >
                                                {s.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </aside>

                        {/* Legal content */}
                        <article
                            data-testid="privacy-content"
                            className="lg:col-span-8 xl:col-span-9 space-y-14 text-slate-700 leading-relaxed"
                        >
                            <Block id="introduction" title="1. Introduction">
                                <p>
                                    {SITE.appName} is a family organisation
                                    platform that helps households manage
                                    shared calendars, task lists, important
                                    documents, medical information and private
                                    photos. Because {SITE.appName} may hold
                                    sensitive information about your family —
                                    including special personal information such
                                    as health data and information relating to
                                    children — we take a conservative,
                                    privacy-first approach to everything we do.
                                </p>
                                <p>
                                    By downloading, installing or using{" "}
                                    {SITE.appName}, you acknowledge that you
                                    have read and understood this Privacy
                                    Policy. If you do not agree with any part
                                    of it, please do not use the Service.
                                </p>
                            </Block>

                            <Block
                                id="responsible-party"
                                title="2. Responsible Party & Information Officer"
                            >
                                <p>
                                    The Responsible Party (as defined in POPIA)
                                    for the processing of your Personal
                                    Information is:
                                </p>
                                <div className="not-prose mt-4 rounded-xl bg-slate-50 border border-slate-200 p-5 text-sm">
                                    <div className="font-semibold text-slate-900">
                                        {SITE.company}
                                    </div>
                                    <div className="mt-1">{SITE.address}</div>
                                    <div className="mt-2">
                                        Support:{" "}
                                        <a
                                            className="text-brand-purple font-medium hover:underline"
                                            href={`mailto:${SITE.supportEmail}`}
                                        >
                                            {SITE.supportEmail}
                                        </a>
                                    </div>
                                    <div>
                                        Information Officer:{" "}
                                        <a
                                            className="text-brand-purple font-medium hover:underline"
                                            href={`mailto:${SITE.infoOfficerEmail}`}
                                        >
                                            {SITE.infoOfficerEmail}
                                        </a>
                                    </div>
                                </div>
                                <p className="mt-4">
                                    Our Information Officer is registered with
                                    the Information Regulator of South Africa
                                    and is responsible for overseeing our
                                    compliance with POPIA. You may contact them
                                    directly with any privacy-related enquiry,
                                    access request or complaint.
                                </p>
                            </Block>

                            <Block
                                id="information-collected"
                                title="3. Information We Collect"
                            >
                                <p>
                                    We only collect information that is
                                    necessary to provide, secure and improve
                                    the Service. The categories of Personal
                                    Information we may process include:
                                </p>
                                <ul>
                                    <li>
                                        <strong>Account information:</strong>{" "}
                                        name, email address, phone number,
                                        country, password (hashed with bcrypt),
                                        profile picture and family/household
                                        role.
                                    </li>
                                    <li>
                                        <strong>Family content you create:</strong>{" "}
                                        calendar entries, tasks, lists, notes,
                                        uploaded documents and photos, family
                                        member profiles, emergency contacts,
                                        pets, household assets and staff
                                        records.
                                    </li>
                                    <li>
                                        <strong>Special personal information:</strong>{" "}
                                        health, medical aid, prescriptions,
                                        allergies, healthcare providers and
                                        children's information — only where you
                                        choose to add it and only within your
                                        family workspace.
                                    </li>
                                    <li>
                                        <strong>Device &amp; usage information:</strong>{" "}
                                        device model, operating system, app
                                        version, language, crash logs, audit
                                        logs and anonymised diagnostic data.
                                    </li>
                                    <li>
                                        <strong>Billing information:</strong>{" "}
                                        card last-4 digits only — we{" "}
                                        <strong>never</strong> store full card
                                        numbers. Full payment details are
                                        handled by our payment processor (e.g.
                                        Google Play Billing) directly.
                                    </li>
                                    <li>
                                        <strong>Communication data:</strong>{" "}
                                        messages you send to support and
                                        feedback you submit.
                                    </li>
                                </ul>
                                <p>
                                    We do <strong>not</strong> sell your data,
                                    we do <strong>not</strong> use it for
                                    third-party advertising, and we do{" "}
                                    <strong>not</strong> scan the contents of
                                    your vault for marketing insights.
                                </p>
                            </Block>

                            <Block
                                id="lawful-basis"
                                title="4. Lawful Basis & Purpose"
                            >
                                <p>
                                    We process Personal Information on the
                                    following lawful bases under section 11 of
                                    POPIA:
                                </p>
                                <ul>
                                    <li>
                                        <strong>Consent</strong> — where you
                                        actively opt in (for example, uploading
                                        medical records or enabling notifications).
                                    </li>
                                    <li>
                                        <strong>Contractual necessity</strong>{" "}
                                        — to deliver the Service you have
                                        signed up for.
                                    </li>
                                    <li>
                                        <strong>Legitimate interests</strong>{" "}
                                        — to secure the Service, prevent abuse
                                        and improve reliability.
                                    </li>
                                    <li>
                                        <strong>Legal obligation</strong> —
                                        where we must comply with applicable
                                        laws, including POPIA itself.
                                    </li>
                                </ul>
                                <p>
                                    We use Personal Information to: (a) provide
                                    core features (calendar, lists, documents,
                                    medical hub, photo vault); (b) authenticate
                                    users and secure family workspaces; (c)
                                    provide customer support; (d) send
                                    service-related notifications; (e) diagnose
                                    and fix bugs; and (f) comply with legal
                                    obligations.
                                </p>
                            </Block>

                            <Block
                                id="sharing-storage"
                                title="5. Sharing, Storage & Security"
                            >
                                <p>
                                    Your family's data belongs to your family.
                                    We do not sell, rent or trade Personal
                                    Information. We only share information in
                                    the following limited circumstances:
                                </p>
                                <ul>
                                    <li>
                                        With <strong>members of your own family workspace</strong> as directed by you, using{" "}
                                        <strong>per-entity permissions that you can revoke at any time</strong>.
                                    </li>
                                    <li>
                                        With trusted{" "}
                                        <strong>operators (processors)</strong>{" "}
                                        essential to operation — cloud hosting,
                                        storage, email delivery, payment
                                        processing (e.g. Google Play Billing),
                                        and AI providers used strictly for
                                        summaries you explicitly request. All
                                        operate under contractual
                                        confidentiality obligations and
                                        POPIA-level safeguards.
                                    </li>
                                    <li>
                                        Where required by{" "}
                                        <strong>law, regulation or valid legal process</strong>.
                                    </li>
                                    <li>
                                        In connection with a{" "}
                                        <strong>business transfer</strong>{" "}
                                        (merger, acquisition or restructure)
                                        subject to confidentiality obligations
                                        and prior notice to affected users.
                                    </li>
                                </ul>
                                <p>
                                    All uploaded files and photos are stored in
                                    the{" "}
                                    <strong>Emergent Object Store</strong> and
                                    are encrypted at rest using AES-256, with
                                    TLS 1.2+ in transit. Access is gated by{" "}
                                    <strong>JWT-authenticated sessions</strong>{" "}
                                    and short-lived, cryptographically signed
                                    URLs tied to your session. Passwords are
                                    stored as salted <strong>bcrypt</strong>{" "}
                                    hashes — never in plain text. Family
                                    sharing uses per-entity permissions with
                                    revocation.
                                </p>
                            </Block>

                            <Block
                                id="children-data"
                                title="6. Children's Data"
                            >
                                <p>
                                    {SITE.appName} is designed for family use.
                                    A parent, legal guardian or competent
                                    person may add a child's information to
                                    their private family workspace for the
                                    purpose of household organisation, medical
                                    tracking and safekeeping.
                                </p>
                                <p>
                                    In accordance with sections 34 and 35 of
                                    POPIA, we require that only a parent,
                                    guardian or duly authorised adult adds or
                                    manages information relating to a child. We
                                    will not knowingly allow a child under 18
                                    to create their own independent account, and
                                    we do not use children's information for
                                    marketing or profiling.
                                </p>
                            </Block>

                            <Block
                                id="user-rights"
                                title="7. Your Rights Under POPIA"
                            >
                                <p>
                                    As a Data Subject you have the following
                                    rights, which you may exercise at any time
                                    by contacting our Information Officer at{" "}
                                    <a
                                        className="text-brand-purple font-medium hover:underline"
                                        href={`mailto:${SITE.infoOfficerEmail}`}
                                    >
                                        {SITE.infoOfficerEmail}
                                    </a>
                                    :
                                </p>
                                <ul>
                                    <li>
                                        <strong>Right of access</strong> — to
                                        confirm what Personal Information we
                                        hold about you.
                                    </li>
                                    <li>
                                        <strong>Right to correction</strong>{" "}
                                        or deletion of inaccurate, irrelevant,
                                        excessive, outdated, misleading or
                                        unlawfully obtained information. Delete
                                        your account and all data at any time
                                        via <strong>Account → Delete my account</strong> — see our{" "}
                                        <Link
                                            to="/account-deletion"
                                            className="text-brand-purple font-medium hover:underline"
                                        >
                                            Account &amp; Data Deletion
                                        </Link>{" "}
                                        page.
                                    </li>
                                    <li>
                                        <strong>Right to object</strong> to the
                                        processing of your Personal
                                        Information, on reasonable grounds.
                                    </li>
                                    <li>
                                        <strong>Right to withdraw consent</strong>{" "}
                                        where processing is based on consent.
                                    </li>
                                    <li>
                                        <strong>Right to data portability</strong>{" "}
                                        — request a machine-readable export of
                                        your family data at any time via{" "}
                                        <strong>Account → Export my data</strong>
                                        .
                                    </li>
                                    <li>
                                        <strong>Right to lodge a complaint</strong>{" "}
                                        with the Information Regulator (see
                                        section 10).
                                    </li>
                                </ul>
                                <p>
                                    We will respond to verified requests within
                                    a reasonable period, and in any event
                                    within the time frames prescribed by POPIA.
                                </p>
                            </Block>

                            <Block
                                id="cookies-retention"
                                title="8. Cookies & Retention"
                            >
                                <p>
                                    The {SITE.appName} mobile application uses
                                    a minimal set of on-device tokens (analogous
                                    to cookies) strictly to keep you signed in
                                    and to remember your preferences. Our
                                    marketing website uses only essential and,
                                    where you consent, anonymous analytics
                                    cookies.
                                </p>
                                <p>
                                    We retain Personal Information only for as
                                    long as necessary to provide the Service
                                    and comply with our legal obligations.
                                    Backups are rotated and permanently purged
                                    on a rolling schedule not exceeding 90
                                    days from deletion. If you delete your
                                    account, we will delete or de-identify your
                                    Personal Information within 30 days, subject
                                    to legal hold requirements.
                                </p>
                            </Block>

                            <Block
                                id="cross-border"
                                title="9. Cross-Border Transfers"
                            >
                                <p>
                                    Some of our operators may process data
                                    outside the Republic of South Africa. Where
                                    this occurs, we ensure that the receiving
                                    party is subject to laws, binding corporate
                                    rules or agreements which uphold principles
                                    substantially similar to POPIA, in line
                                    with section 72 of POPIA. Family photos and
                                    documents stored in the Emergent Object
                                    Store are held in encrypted form under such
                                    arrangements.
                                </p>
                            </Block>

                            <Block
                                id="complaints"
                                title="10. Complaints & Regulator Contact"
                            >
                                <p>
                                    We would prefer to resolve any concern
                                    directly. Please contact our Information
                                    Officer at{" "}
                                    <a
                                        className="text-brand-purple font-medium hover:underline"
                                        href={`mailto:${SITE.infoOfficerEmail}`}
                                    >
                                        {SITE.infoOfficerEmail}
                                    </a>{" "}
                                    in the first instance.
                                </p>
                                <p>
                                    You also have the right to complain to the
                                    Information Regulator:
                                </p>
                                <div className="not-prose mt-3 rounded-xl bg-slate-50 border border-slate-200 p-5 text-sm">
                                    <div className="font-semibold text-slate-900">
                                        The Information Regulator (South Africa)
                                    </div>
                                    <div>
                                        JD House, 27 Stiemens Street, Braamfontein,
                                        Johannesburg, 2001
                                    </div>
                                    <div className="mt-1">
                                        General enquiries:{" "}
                                        <a
                                            className="text-brand-purple font-medium hover:underline"
                                            href="mailto:enquiries@inforegulator.org.za"
                                        >
                                            enquiries@inforegulator.org.za
                                        </a>
                                    </div>
                                    <div>
                                        POPIA complaints:{" "}
                                        <a
                                            className="text-brand-purple font-medium hover:underline"
                                            href="mailto:POPIAComplaints@inforegulator.org.za"
                                        >
                                            POPIAComplaints@inforegulator.org.za
                                        </a>
                                    </div>
                                    <div className="mt-2 inline-flex items-center gap-1">
                                        <a
                                            className="text-brand-purple font-medium hover:underline"
                                            href="https://inforegulator.org.za"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            inforegulator.org.za
                                        </a>
                                        <ExternalLink className="h-3.5 w-3.5" />
                                    </div>
                                </div>
                            </Block>

                            <Block
                                id="changes"
                                title="11. Changes to this Policy"
                            >
                                <p>
                                    We may update this Privacy Policy from time
                                    to time to reflect changes in the Service,
                                    the law or industry practice. Any material
                                    change will be communicated in-app or by
                                    email at least 14 days before it takes
                                    effect. The "Effective date" at the top of
                                    this page always reflects the current
                                    version.
                                </p>
                            </Block>

                            <Block id="contact" title="12. Contact Us">
                                <p>
                                    Questions about this Privacy Policy or your
                                    Personal Information? We're a real team of
                                    humans and we'd rather hear from you
                                    directly.
                                </p>
                                <div className="not-prose mt-4 rounded-xl bg-slate-50 border border-slate-200 p-5 text-sm">
                                    <div className="font-semibold text-slate-900">
                                        {SITE.company}
                                    </div>
                                    <div>{SITE.address}</div>
                                    <div className="mt-2">
                                        Support:{" "}
                                        <a
                                            className="text-brand-purple font-medium hover:underline"
                                            href={`mailto:${SITE.supportEmail}`}
                                        >
                                            {SITE.supportEmail}
                                        </a>
                                    </div>
                                    <div>
                                        Information Officer:{" "}
                                        <a
                                            className="text-brand-purple font-medium hover:underline"
                                            href={`mailto:${SITE.infoOfficerEmail}`}
                                        >
                                            {SITE.infoOfficerEmail}
                                        </a>
                                    </div>
                                </div>
                            </Block>
                        </article>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

const Block = ({ id, title, children }) => (
    <section id={id} data-testid={`section-${id}`} className="scroll-mt-28">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {title}
        </h2>
        <div className="mt-5 prose prose-slate max-w-none prose-p:leading-relaxed prose-ul:pl-5 prose-li:my-1 prose-strong:text-slate-900">
            {children}
        </div>
    </section>
);
